import { Constants, Utils, CookieService } from "../../../shared";
import axios from "axios";

export const CampaignDetailService = {

    // update the campaign obj as per the existing code in campaign details component
    updateCampaignDataObj: (campaignData) => {        
        campaignData['mediaBudget'] = campaignData['media_attributes']['media_budget'] || '';
        campaignData['channels'] = campaignData['media_attributes']['channels'] || '';
        campaignData['clientName'] = campaignData['client_name'] || '';
        campaignData['fiscal year'] = campaignData['fiscal_year'] || '';
        campaignData['campaign period'] = campaignData['campaign_period'] || '';
        campaignData['line of business/business/brand'] = campaignData['line_of_business'] || '';
        campaignData['product/product line'] = campaignData['product_line'] || '';
        campaignData['campaign objective'] = campaignData['campaign_objective'] || 'Awareness';
        campaignData['country'] = campaignData['country'] || '';
        campaignData['currency'] = campaignData['currency'] || '';
        campaignData['market/region'] = campaignData['region'] || '';
        campaignData['funnel stage'] = campaignData['funnel stage'] || '';
        campaignData['campaign_taxonomy_display'] = campaignData['campaign_taxonomy_display'] || '';
        campaignData['campaign_total_budget'] = campaignData['campaign_total_budget'] || '';
        campaignData['campaign_total_budget_local'] = campaignData['campaign_total_budget_local'] || '';
        campaignData['campaign_fees'] = campaignData['campaign_fees'] || '';
        campaignData['startDate'] = campaignData['start_date'] || '';
        campaignData['endDate'] = campaignData['end_date'] || '';
        campaignData['campaign name/description'] = campaignData['campaign_name'] || '';
        campaignData['campaignTaxonomy'] = campaignData['campaign_taxonomy'] || '-';
        campaignData['taxonomy_schema'] = campaignData['taxonomy_schema'] || '';
        campaignData['local_currency_name'] = campaignData['local_currency_name'];
        return campaignData;
    },

    /**
     * Function to sort the table data based on the key and direction
     * @param {Array} tableData - Array of objects to be sorted
     * @param {String} key - Key based on which the data should be sorted
     * @param {String} dir - Direction of sorting (asc or desc)
     * @returns {Array} - Sorted table data
     */
    sortTableData: (tableData, key, dir) => {
        const sortLevel = (data) => {
            let sortedData = [];
            //for keys with values as type number
            if (["budget", "totalBudget", "authorized_amount", "totalFeeAmount", "amount_sent"].includes(key)) {
                sortedData = Utils.sortNumber(data, key, dir);
            } else if(key === 'startDate' || key === 'endDate') {
                sortedData = Utils.sortDate(data, key, dir);
            } else {
                sortedData = Utils.sortString(data, key, dir);
            }
            for (const obj of sortedData) {
                if (obj.nestedData) {
                    obj.nestedData = sortLevel(obj.nestedData);
                }
            }
            return sortedData;
        }
        return sortLevel(tableData);
    },

    /**
     * Creates a child campaign by sending a POST request with the provided payload.
     *
     * @param {Object} payload - The payload object containing the campaign data.
     * @returns {Promise<Object>} - A promise that resolves to the response object or rejects with an error.
     */
    createChildCampaign: (payload) => {
        return axios
            .post(Constants.CampaignDataUrl, payload,
                {
                    headers: {
                        ansid: CookieService.getCookie("ANsid"),
                        clientid: Utils.getRouteParam("clientid")
                    }
                })
            .then((response) => { return response; },
                (error) => { return Promise.reject(error); }
            );
    },

    /**
     * Saves the child campaign data.
     *
     * This method sends a PUT request to save the child campaign data using the provided payload.
     * It includes necessary headers such as `ansid` and `clientid` from cookies.
     * The method returns a promise that resolves with the response or rejects with an error.
     *
     * @param {Object} payload - The payload containing the child campaign data to be saved.
     * @returns {Promise<Object>} - A promise that resolves with the response or rejects with an error.
     */
    saveChildCampaign: (payload) => {
        return axios
            .put(Constants.CampaignDataUrl, payload,
                {
                    headers: {
                        ansid: CookieService.getCookie("ANsid"),
                        clientid: Utils.getRouteParam("clientid")
                    }
                })
            .then((response) => { return response; },
                (error) => { return Promise.reject(error); }
            );
    },

    /**
     * Removes commas from a string and converts it to a number.
     *
     * @param {string|number} value - The value to be converted.
     * @returns {number} - The converted number.
     */
    removeCommaAndConvertToNumber: (value) => {
        return Number(value?.toString()?.replace(',', ''))
    },

    /**
     * Updates the budget table data.
     *
     * @param {Array<Object>} onlineArr - The array of online data.
     * @param {Array<Object>} printArr - The array of print data.
     * @param {boolean} isLocal - Flag indicating if the currency is local.
     * @param {string} currencyName - The name of the currency.
     * @param {Function} calculateTotals - Function to calculate totals.
     * @returns {Object} - The updated budget table data.
     */
    updateBudgetTableData: (onlineArr, printArr, isLocal, currencyName) => {
        const budgetTableData = [{ title: '', netMedia: 'Net Media', totalMedia: 'Total Media', amountSent: 'Amount Sent', status: 'Status' }];
        let onlineTableObj = CampaignDetailService.getDataForTable(onlineArr, 'Online', isLocal, currencyName);
        let printTableObj = CampaignDetailService.getDataForTable(printArr, 'Print', isLocal, currencyName);
        let currancyName = isLocal && currencyName ? currencyName : 'USD';
        let tableObj = {
            title: 'Total',
            netMedia: (onlineTableObj.netMedia + printTableObj.netMedia),
            totalMedia: (onlineTableObj.totalMedia + printTableObj.totalMedia),
            amountSent: (onlineTableObj.amountSent + printTableObj.amountSent)
        };
        let totalTableObj = CampaignDetailService.updateAmountsAsCurrencyStrings({ ...tableObj }, currancyName);
        printTableObj = CampaignDetailService.updateAmountsAsCurrencyStrings(printTableObj, currancyName);
        onlineTableObj = CampaignDetailService.updateAmountsAsCurrencyStrings(onlineTableObj, currancyName);
    
        budgetTableData.push(totalTableObj, onlineTableObj, printTableObj);
        return tableObj ;
    },

    /**
     * Gets data for the table.
     *
     * @param {Array<Object>} data - The array of data.
     * @param {string} title - The title for the table.
     * @param {Function} calculateTotals - Function to calculate totals.
     * @returns {Object} - The table data object.
     */
    getDataForTable: (data, title, isLocal, currencyName) => {
        const totals = CampaignDetailService.calculateTotals(data, isLocal, currencyName);
        return {
            title: title,
            netMedia: totals.netMedia,
            totalMedia: totals.totalAmount,
            amountSent: totals.amountSent
        };
    },

    /**
     * Updates amounts as currency strings.
     *
     * @param {Object} obj - The object containing amounts.
     * @param {string} currancyName - The name of the currency.
     * @returns {Object} - The updated object with amounts as currency strings.
     */
    updateAmountsAsCurrencyStrings: (obj, currancyName) => {
        obj.netMedia = obj.netMedia?.toLocaleString('en-US', { style: "currency", currency: currancyName });
        obj.totalMedia = obj.totalMedia?.toLocaleString('en-US', { style: "currency", currency: currancyName });
        obj.amountSent = obj.amountSent?.toLocaleString('en-US', { style: "currency", currency: currancyName });
        return obj;
    },

    /**
     * Calculates totals for the given data.
     *
     * This method calculates the total net media, total amount, and amount sent for the provided data.
     * It uses different keys for local and non-local currencies based on the `isLocal` flag and `currencyName`.
     *
     * @param {Array<Object>} data - The array of data objects to calculate totals for.
     * @param {boolean} isLocal - Flag indicating if the currency is local.
     * @param {string} currencyName - The name of the currency.
     * @returns {Object} - An object containing the calculated totals: netMedia, totalAmount, and amountSent.
     */
    calculateTotals: (data, isLocal, currencyName) => {
        return data.reduce((acc, element) => {
            let budgetKey = 'budget';
            let totalBudgetKey = 'totalBudget';
            let amountSentKey = 'amount_sent';
            if (isLocal && currencyName) {
                budgetKey = element?.budgetLocal ? 'budgetLocal' : 'budget';
                totalBudgetKey = element?.totalBudgetLocal ? 'totalBudgetLocal' : 'totalBudget';
                amountSentKey = element?.amount_sent_local ? 'amount_sent_local' : 'amount_sent';
            }
            return {
                netMedia: Number(acc.netMedia) + (Number(element?.[budgetKey]) || 0),
                totalAmount: Number(acc.totalAmount) + (Number(element?.[totalBudgetKey]) || 0),
                amountSent: Number(acc.amountSent) + (Number(element?.[amountSentKey]) || 0),
            };
        }, { netMedia: 0, totalAmount: 0, amountSent: 0 });
    },

    downloadTemplate: () => {
        const url = `${Constants.DownloadCampaignTemplateUrl}/${Utils.getRouteParam("clientid")}`;
        return axios.get(url, {
            headers: {
                ansid: CookieService.getCookie("ANsid"),
                clientid: Utils.getRouteParam("clientid")
            }
        });
    },

    checkJobStatusAndDownloadTemplate: (jobId) => {
        const url = `${Constants.CampaignSyncUrl}/${Utils.getRouteParam("clientid")}/async-job-status/${jobId}`;
        return axios.get(url, {
            headers: {
                ansid: CookieService.getCookie("ANsid"),
                clientid: Utils.getRouteParam("clientid")
            }
        });
    },

    uploadCampaignTemplate: (fileData) => {
        return axios.post(Constants.UploadCampaignTemplateUrl, fileData, {
            headers: {
                ansid: CookieService.getCookie("ANsid"),
                clientid: Utils.getRouteParam("clientid")
            }
        });
    },

    getOrderedData: (campaignId) => {
        return axios.get(`${Constants.CampaignDataUrl}/${campaignId}`, {
            params: {clientId: Utils.getRouteParam("clientid"), order_data: true},
            headers: {
                ansid: CookieService.getCookie("ANsid"),
                clientid: Utils.getRouteParam("clientid")
            }
        });
    }
}